/**
 * Cart Patch — Performance fixes for high-traffic add-to-cart
 *
 * 1. Fetch + XHR Monitor: intercepts ALL requests, classifies by endpoint,
 *    and provides a console report to identify which sources cause 429 errors.
 *    Must load BEFORE content_for_header to intercept all 3rd-party scripts.
 * 2. Proactive Request Throttle: spaces out Shopify API requests to stay
 *    under Shopify's rate limit. Once 429 hits, Shopify triggers bot
 *    verification that blocks the entire session — so prevention is critical.
 *    Strategy: track request budget, delay non-essential reads when budget is tight.
 * 3. Eliminates the redundant GET /cart.js fetch by extracting cart state
 *    from the Section Rendering API HTML already returned by POST /cart/add.js.
 * 4. Provides safeCartFetch() for cart write operations with retry logic.
 */
(function () {

  // ─── Fetch Monitor ────────────────────────────────────────────────

  var SHOPIFY_ENDPOINTS = [
    { pattern: /\/cart\/add\.js/, category: 'cart-add', label: 'POST /cart/add.js' },
    { pattern: /\/cart\/add($|\?)/, category: 'cart-add-html', label: 'POST /cart/add (full-page)' },
    { pattern: /\/cart\/change\.js/, category: 'cart-change', label: 'POST /cart/change.js' },
    { pattern: /\/cart\/change($|\?)/, category: 'cart-change', label: 'POST /cart/change' },
    { pattern: /\/cart\/update\.js/, category: 'cart-update', label: 'POST /cart/update.js' },
    { pattern: /\/cart\.js/, category: 'cart-get', label: 'GET /cart.js' },
    { pattern: /\/cart\//, category: 'cart-other', label: 'Other /cart/' },
    { pattern: /\/recommendations\/products/, category: 'recommend', label: '/recommendations/products' },
    { pattern: /section_id=/, category: 'sections', label: 'Section Rendering (?section_id=)' },
    { pattern: /\/search\/suggest/, category: 'search', label: 'Predictive Search' },
    { pattern: /\/search\?/, category: 'search', label: 'Search' },
    { pattern: /shipping_rates\.json/, category: 'shipping', label: 'Shipping Rates' },
    { pattern: /\/api\/collect/, category: 'analytics', label: '/api/collect' },
    { pattern: /\/api\/.+\/graphql/, category: 'graphql', label: 'Storefront API (graphql)' },
    { pattern: /\/cdn\/shopifycloud\/portable-wallets/, category: 'shopify-wallets', label: 'Shopify Portable Wallets' },
    { pattern: /\/services\/login_with_shop/, category: 'shopify-login', label: 'Shopify Login' }
  ];

  var EXTERNAL_SERVICES = [
    { pattern: /google-analytics\.com|\/g\/collect|googletagmanager/, label: 'Google Analytics' },
    { pattern: /googleadservices\.com|\/pagead\/conversion|\/pagead\/viewthroughconversion/, label: 'Google Ads' },
    { pattern: /googlesyndication/, label: 'Google AdSense' },
    { pattern: /facebook\.com|fbcdn/, label: 'Facebook/Meta' },
    { pattern: /analytics\.twitter|t\.co/, label: 'Twitter/X' },
    { pattern: /snap\.com|sc-analytics/, label: 'Snapchat' },
    { pattern: /pinterest\.com/, label: 'Pinterest' },
    { pattern: /tiktok\.com/, label: 'TikTok' },
    { pattern: /shopifysvc\.com/, label: 'Shopify Telemetry' },
    { pattern: /shopify\.com\/s\/files/, label: 'Shopify CDN' },
    { pattern: /loox\.io/, label: 'Loox Reviews' },
    { pattern: /bloy/, label: 'Bloy' },
    { pattern: /otlp-http/, label: 'OpenTelemetry' },
    { pattern: /\/v1\/metrics/, label: 'App Metrics' },
    { pattern: /klaviyo/, label: 'Klaviyo' },
    { pattern: /yotpo/, label: 'Yotpo' },
    { pattern: /judgeme/, label: 'Judge.me' }
  ];

  var WRAPPER_SCRIPTS = [
    'cart-patch.js',
    'shopifyChatV1Widget.js',
    'shop_events_listener',
    'ba9664f44w6a62cec8p04af10e4mb91e3447m.js',
    'loox.io',
    'bloy'
  ];

  // Categories that are non-essential reads — safe to defer when budget is tight
  var DEFERRABLE_CATEGORIES = ['sections', 'recommend', 'search', 'cart-get'];

  var monitor = {
    _entries: [],
    _origFetch: window.fetch.bind(window),

    classify: function (url, init) {
      var urlString = typeof url === 'string' ? url : (url && url.url ? url.url : '');
      var isShopify = urlString.indexOf(window.location.origin) === 0 || urlString.charAt(0) === '/';

      var isAjaxApi = false;
      if (init && init.headers) {
        var accept = '';
        if (typeof init.headers.get === 'function') {
          accept = init.headers.get('Accept') || '';
        } else if (init.headers['Accept'] || init.headers['accept']) {
          accept = init.headers['Accept'] || init.headers['accept'];
        }
        if (/application\/(json|javascript)/i.test(accept)) {
          isAjaxApi = true;
        }
      }

      if (isShopify) {
        for (var i = 0; i < SHOPIFY_ENDPOINTS.length; i++) {
          if (SHOPIFY_ENDPOINTS[i].pattern.test(urlString)) {
            var result = { category: SHOPIFY_ENDPOINTS[i].category, label: SHOPIFY_ENDPOINTS[i].label, isShopify: true };
            if (isAjaxApi) {
              if (result.category === 'cart-add-html') {
                result.category = 'cart-add';
                result.label = 'POST /cart/add.js';
              } else if (result.category === 'cart-change' && /\/cart\/change($|\?)/.test(urlString)) {
                result.category = 'cart-change';
                result.label = 'POST /cart/change.js';
              }
            }
            return result;
          }
        }
        return { category: 'other-shopify', label: urlString.substring(0, 60), isShopify: true };
      }

      var domain = '';
      try {
        domain = new URL(urlString, window.location.origin).hostname;
      } catch (e) {}

      var fullString = domain + ' ' + urlString;
      for (var j = 0; j < EXTERNAL_SERVICES.length; j++) {
        if (EXTERNAL_SERVICES[j].pattern.test(fullString)) {
          return { category: 'external', label: EXTERNAL_SERVICES[j].label, isShopify: false };
        }
      }

      return { category: 'external', label: domain || urlString.substring(0, 40), isShopify: false };
    },

    isDeferrable: function (classified) {
      return DEFERRABLE_CATEGORIES.indexOf(classified.category) !== -1;
    },

    getCaller: function () {
      try {
        var stack = new Error().stack || '';
        var lines = stack.split('\n');
        for (var i = 3; i < lines.length; i++) {
          var line = lines[i].trim();
          var isWrapper = false;
          for (var w = 0; w < WRAPPER_SCRIPTS.length; w++) {
            if (line.indexOf(WRAPPER_SCRIPTS[w]) !== -1) {
              isWrapper = true;
              break;
            }
          }
          if (isWrapper) continue;

          var funcMatch = line.match(/at\s+(\S+)\s+\(/);
          var fileMatch = line.match(/\(?(https?:\/\/[^:)]+|<anonymous>):(\d+):(\d+)\)?/);

          if (fileMatch) {
            var src = fileMatch[1];
            var lineNum = fileMatch[2];
            if (src === '<anonymous>') {
              var funcName = funcMatch ? funcMatch[1] : '';
              if (funcName && funcName !== 'Object.<anonymous>' && funcName !== '<anonymous>') {
                return funcName + ' (inline)';
              }
              return 'inline-script';
            }
            var parts = src.split('/');
            var filename = parts[parts.length - 1];
            return filename + ':' + lineNum;
          }
        }
      } catch (e) {}
      return 'unknown';
    },

    log: function (url, method, duration, status, classified, caller) {
      this._entries.push({
        time: Date.now(),
        url: typeof url === 'string' ? url : (url && url.url ? url.url : ''),
        method: method || 'GET',
        duration: duration,
        status: status,
        category: classified.category,
        label: classified.label,
        isShopify: classified.isShopify,
        caller: caller || ''
      });
    },

    report: function () {
      var stats = {};
      var recentErrors = [];
      var now = Date.now();

      for (var i = 0; i < this._entries.length; i++) {
        var entry = this._entries[i];
        if (!stats[entry.category]) {
          stats[entry.category] = { label: entry.label, count: 0, errors: 0, avgMs: 0, totalMs: 0 };
        }
        stats[entry.category].count++;
        stats[entry.category].totalMs += entry.duration;
        if (entry.status >= 400) {
          stats[entry.category].errors++;
          if (now - entry.time < 60000) {
            recentErrors.push(entry);
          }
        }
      }

      for (var key in stats) {
        stats[key].avgMs = Math.round(stats[key].totalMs / stats[key].count);
      }

      console.group('%c🛒 Shopify Fetch Monitor Report', 'font-size:14px;font-weight:bold;color:#2563eb');

      console.group('%cRequest Summary (by API type)', 'font-weight:bold');
      console.table(stats);
      console.groupEnd();

      var shopifyCount = 0, externalCount = 0;
      for (var k = 0; k < this._entries.length; k++) {
        if (this._entries[k].isShopify) shopifyCount++; else externalCount++;
      }
      console.log(
        '%c⚡ Only same-domain (Shopify) requests share the 429 rate limit. ' +
        'Shopify: %d, External: %d',
        'color:#059669', shopifyCount, externalCount
      );

      // Budget status
      var budgetUsed = throttle.recentCount();
      console.log(
        '%c🪣 Request budget: %d/%d used in last 10s',
        budgetUsed >= THROTTLE_SOFT_LIMIT ? 'color:#dc2626;font-weight:bold' : 'color:#059669',
        budgetUsed, THROTTLE_SOFT_LIMIT
      );

      if (recentErrors.length > 0) {
        console.group('%c⚠️ Errors in last 60s', 'font-weight:bold;color:#dc2626');
        for (var j = 0; j < recentErrors.length; j++) {
          var err = recentErrors[j];
          console.warn(
            err.method + ' ' + err.status + ' ' + err.label + ' (' + err.duration + 'ms)',
            '\n  from:', err.caller,
            '\n  url:', err.url
          );
        }
        console.groupEnd();
      }

      var recentShopify = this._entries.filter(function (e) {
        return now - e.time < 30000 && e.isShopify;
      });
      if (recentShopify.length > 0) {
        console.group('%c📊 Shopify requests (last 30s)', 'font-weight:bold;color:#059669');
        var timeline = recentShopify.map(function (e) {
          var ts = new Date(e.time);
          var shortUrl = '';
          try {
            var u = new URL(e.url, window.location.origin);
            shortUrl = u.pathname + u.search;
            if (shortUrl.length > 60) shortUrl = shortUrl.substring(0, 57) + '...';
          } catch (ex) {
            shortUrl = e.url.substring(0, 60);
          }
          return {
            time: ts.getHours().toString().padStart(2, '0') + ':' + ts.getMinutes().toString().padStart(2, '0') + ':' + ts.getSeconds().toString().padStart(2, '0') + '.' + Math.floor(ts.getMilliseconds() / 100),
            method: e.method,
            status: e.status,
            ms: e.duration,
            from: e.caller,
            url: shortUrl
          };
        });
        console.table(timeline);
        console.groupEnd();
      }

      var callerStats = {};
      for (var m = 0; m < this._entries.length; m++) {
        var entry2 = this._entries[m];
        var c = entry2.caller || 'unknown';
        if (!callerStats[c]) {
          callerStats[c] = { count: 0, errors: 0, shopify: 0, categories: {} };
        }
        callerStats[c].count++;
        if (entry2.isShopify) callerStats[c].shopify++;
        if (entry2.status >= 400) callerStats[c].errors++;
        var cat = entry2.category;
        callerStats[c].categories[cat] = (callerStats[c].categories[cat] || 0) + 1;
      }

      console.group('%c🔍 Breakdown by Source', 'font-weight:bold;color:#7c3aed');
      var callerRows = [];
      for (var callerKey in callerStats) {
        var cs = callerStats[callerKey];
        var topCategories = Object.keys(cs.categories).sort(function (a, b) { return cs.categories[b] - cs.categories[a]; });
        callerRows.push({
          source: callerKey,
          total: cs.count,
          shopify: cs.shopify,
          errors: cs.errors,
          topCategory: topCategories[0] + '(' + cs.categories[topCategories[0]] + ')'
        });
      }
      callerRows.sort(function (a, b) { return b.shopify - a.shopify; });
      console.table(callerRows);
      console.groupEnd();

      var externalStats = {};
      for (var n = 0; n < this._entries.length; n++) {
        var entry3 = this._entries[n];
        if (entry3.isShopify) continue;
        var svc = entry3.label || 'other';
        if (!externalStats[svc]) externalStats[svc] = 0;
        externalStats[svc]++;
      }
      var extKeys = Object.keys(externalStats);
      if (extKeys.length > 0) {
        console.group('%c🌐 External services (do NOT cause Shopify 429)', 'font-weight:bold;color:#6b7280');
        var extRows = extKeys.map(function (k) {
          return { service: k, requests: externalStats[k] };
        }).sort(function (a, b) { return b.requests - a.requests; });
        console.table(extRows);
        console.groupEnd();
      }

      console.groupEnd();
      return stats;
    },

    reset: function () {
      this._entries = [];
      console.log('%c🛒 Fetch Monitor reset', 'color:#2563eb');
    }
  };

  // ─── Proactive Request Throttle ───────────────────────────────────
  // Shopify uses a leaky bucket (~40 tokens, ~2/s refill) shared by ALL
  // same-domain requests. Once you hit 429, Shopify triggers bot
  // verification that blocks the entire session until page refresh.
  //
  // Strategy: NEVER hit 429. Track our request budget and proactively
  // delay non-essential requests when budget is tight.
  //
  // Priority:
  //   1. ESSENTIAL: cart writes (POST /cart/add.js, /cart/change.js, etc.)
  //      — these must go through, the user explicitly requested them
  //   2. IMPORTANT: variant switch reads (renderProductInfo)
  //      — user expects to see variant info, but can wait briefly
  //   3. DEFERRABLE: quantity rules, recommendations, search
  //      — nice-to-have, safe to delay 1-3 seconds

  var THROTTLE_WINDOW_MS = 10000;    // Rolling 10-second window
  var THROTTLE_SOFT_LIMIT = 6;       // Start deferring non-essential at this count
  var THROTTLE_HARD_LIMIT = 10;      // Defer ALL non-essential at this count
  var THROTTLE_MIN_SPACING_MS = 300; // Min gap between Shopify requests

  var throttle = {
    _timestamps: [],
    _lastRequestTime: 0,
    _deferredCount: 0,

    // Record that a Shopify request was made
    record: function () {
      var now = Date.now();
      this._timestamps.push(now);
      this._lastRequestTime = now;
      this._clean();
    },

    // Count Shopify requests in the rolling window
    recentCount: function () {
      this._clean();
      return this._timestamps.length;
    },

    _clean: function () {
      var cutoff = Date.now() - THROTTLE_WINDOW_MS;
      this._timestamps = this._timestamps.filter(function (t) {
        return t > cutoff;
      });
    },

    // Check if a request should be delayed
    // Returns: { shouldDefer: bool, waitMs: number }
    check: function (classified) {
      if (!classified.isShopify) return { shouldDefer: false, waitMs: 0 };

      var recent = this.recentCount();

      // Cart writes are essential — never defer, but enforce minimum spacing
      if (!monitor.isDeferrable(classified)) {
        var timeSinceLast = Date.now() - this._lastRequestTime;
        if (timeSinceLast < THROTTLE_MIN_SPACING_MS) {
          return { shouldDefer: true, waitMs: THROTTLE_MIN_SPACING_MS - timeSinceLast };
        }
        return { shouldDefer: false, waitMs: 0 };
      }

      // Deferrable reads: delay based on how close we are to the limit
      if (recent >= THROTTLE_HARD_LIMIT) {
        // Over hard limit — wait until oldest request expires
        var waitUntil = this._timestamps[0] + THROTTLE_WINDOW_MS + 100;
        return { shouldDefer: true, waitMs: Math.max(waitUntil - Date.now(), 1000) };
      }

      if (recent >= THROTTLE_SOFT_LIMIT) {
        // Over soft limit — add progressive delay
        var overBy = recent - THROTTLE_SOFT_LIMIT + 1;
        var delay = overBy * 500; // 500ms, 1000ms, 1500ms...
        return { shouldDefer: true, waitMs: delay };
      }

      // Under soft limit — just enforce minimum spacing
      var timeSinceLast2 = Date.now() - this._lastRequestTime;
      if (timeSinceLast2 < THROTTLE_MIN_SPACING_MS) {
        return { shouldDefer: true, waitMs: THROTTLE_MIN_SPACING_MS - timeSinceLast2 };
      }

      return { shouldDefer: false, waitMs: 0 };
    }
  };

  // ─── 429 Auto-Refresh ─────────────────────────────────────────────
  // Once Shopify returns 429, the session is dead — every subsequent
  // request will also 429 until bot verification is passed.
  // Strategy: detect 429 on a cart write → immediately reload the page
  // to trigger Shopify's bot verification challenge.
  // Guard: use sessionStorage to prevent refresh loops.

  var CART_WRITE_CATEGORIES = ['cart-add', 'cart-change', 'cart-update', 'cart-get'];

  function handle429(classified, method, duration, caller, url) {
    var isCartWrite = CART_WRITE_CATEGORIES.indexOf(classified.category) !== -1;

    console.warn(
      '%c⚠️ 429 Rate Limited! %c' + method + ' ' + classified.label + ' (' + duration + 'ms) from ' + caller,
      'font-weight:bold;color:#dc2626',
      'color:#9ca3af',
      '\n  URL:', url
    );

    if (isCartWrite) {
      // Cart write 429 = session is dead, user can't add to cart
      // Check if we already refreshed recently to avoid loops
      var lastRefresh = parseInt(sessionStorage.getItem('_429_refresh_ts') || '0', 10);
      if (Date.now() - lastRefresh < 30000) {
        // Already refreshed within 30s — don't loop
        console.warn('%c⚠️ 429 loop detected — not refreshing again. Please refresh manually.', 'color:#dc2626;font-weight:bold');
        return;
      }
      sessionStorage.setItem('_429_refresh_ts', Date.now().toString());
      console.warn('%c🔄 429 on cart write — reloading page to restore session', 'color:#f59e0b;font-weight:bold');
      setTimeout(function () {
        window.location.reload();
      }, 500);
    }
  }

  // ─── Monkey-patch window.fetch with throttle ──────────────────────

  window.fetch = function (input, init) {
    var classified = monitor.classify(input, init);
    var method = (init && init.method) || 'GET';
    var caller = monitor.getCaller();

    // For Shopify requests: check throttle before sending
    if (classified.isShopify) {
      var throttleCheck = throttle.check(classified);

      if (throttleCheck.shouldDefer && throttleCheck.waitMs > 0) {
        // Delay the request
        return new Promise(function (resolve) {
          setTimeout(function () {
            throttle.record();
            var startTime = performance.now();
            monitor._origFetch(input, init).then(
              function (response) {
                var duration = Math.round(performance.now() - startTime);
                monitor.log(input, method, duration, response.status, classified, caller);
                if (response.status === 429 && classified.isShopify) {
                  var url = typeof input === 'string' ? input : (input && input.url ? input.url : '');
                  handle429(classified, method, duration, caller, url);
                }
                return response;
              },
              function (error) {
                var duration = Math.round(performance.now() - startTime);
                monitor.log(input, method, duration, 0, classified, caller);
                throw error;
              }
            ).then(resolve);
          }, throttleCheck.waitMs);
        });
      }

      throttle.record();
    }

    var startTime = performance.now();
    return monitor._origFetch(input, init).then(
      function (response) {
        var duration = Math.round(performance.now() - startTime);
        monitor.log(input, method, duration, response.status, classified, caller);

        if (response.status === 429 && classified.isShopify) {
          var url2 = typeof input === 'string' ? input : (input && input.url ? input.url : '');
          handle429(classified, method, duration, caller, url2);
        }

        if (duration > 3000 && classified.isShopify) {
          console.warn(
            '%c🐢 Slow request %c' + method + ' ' + classified.label + ' (' + duration + 'ms) from ' + caller,
            'font-weight:bold;color:#f59e0b',
            'color:#9ca3af'
          );
        }

        return response;
      },
      function (error) {
        var duration = Math.round(performance.now() - startTime);
        monitor.log(input, method, duration, 0, classified, caller);
        throw error;
      }
    );
  };

  // ─── XHR Interception ─────────────────────────────────────────────

  var origXhrOpen = XMLHttpRequest.prototype.open;
  var origXhrSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url) {
    this._monitorUrl = url;
    this._monitorMethod = (method || 'GET').toUpperCase();
    this._monitorStart = performance.now();
    return origXhrOpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function () {
    var xhr = this;
    if (xhr._monitorUrl) {
      var classified = monitor.classify(xhr._monitorUrl);
      if (classified.isShopify) {
        throttle.record();
      }
      var caller = monitor.getCaller();
      xhr.addEventListener('loadend', function () {
        var duration = Math.round(performance.now() - (xhr._monitorStart || performance.now()));
        var status = xhr.status || 0;
        monitor.log(xhr._monitorUrl, xhr._monitorMethod, duration, status, classified, caller);

        if (status === 429 && classified.isShopify) {
          handle429(classified, xhr._monitorMethod, duration, caller, xhr._monitorUrl);
        }
      });
    }
    return origXhrSend.apply(this, arguments);
  };

  // Expose for DevTools usage
  window.__cartMonitor = monitor;
  window.__throttle = throttle;

  // Auto-report every 60 seconds
  setInterval(function () {
    if (monitor._entries.length > 0) {
      monitor.report();
    }
  }, 60000);

  // ─── Utility Functions (require theme.utils) ──────────────────────

  function registerUtilities() {
    if (!window.theme || !theme.utils) return;

    // waitForRateLimit: now proactively checks throttle budget before sending.
    // For essential writes: minimal spacing only.
    // For deferrable reads: waits if budget is tight.
    theme.utils.waitForRateLimit = function (isEssential) {
      // Create a fake classified to check throttle
      var fakeClassified = isEssential
        ? { category: 'cart-add', isShopify: true }
        : { category: 'sections', isShopify: true };
      var check = throttle.check(fakeClassified);
      if (check.shouldDefer && check.waitMs > 0) {
        console.warn('%c⏳ Throttle — waiting ' + check.waitMs + 'ms (budget: ' + throttle.recentCount() + '/' + THROTTLE_SOFT_LIMIT + ')', 'color:#f59e0b');
        return new Promise(function (resolve) { setTimeout(resolve, check.waitMs); });
      }
      return Promise.resolve();
    };

    theme.utils.extractItemCountFromSections = function (sections, sectionId) {
      if (!sections || !sectionId) return undefined;
      var html = sections[sectionId];
      if (!html) return undefined;
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var cartCountEl = doc.querySelector('cart-count');
      if (!cartCountEl) return undefined;
      var count = parseInt(cartCountEl.textContent, 10);
      return isNaN(count) ? undefined : count;
    };

    theme.utils.buildCartStateFromAddResponse = function (parsedState, sectionId) {
      var itemCount = theme.utils.extractItemCountFromSections(parsedState.sections, sectionId);
      return Object.assign({}, parsedState, { item_count: itemCount });
    };

    // safeCartFetch: for cart write operations.
    // 429 triggers auto-refresh via handle429, so no retry logic needed here.
    theme.utils.safeCartFetch = function (url, config) {
      return fetch(url, config).then(function (response) {
        if (response.ok) return response.json();
        throw new Error(theme.cartStrings.error);
      });
    };
  }

  function tryRegisterUtilities() {
    if (!window.theme || !theme.utils) {
      setTimeout(tryRegisterUtilities, 50);
      return;
    }
    registerUtilities();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', tryRegisterUtilities);
  } else {
    tryRegisterUtilities();
  }
})();
